import React, { useCallback, useEffect, useMemo, useContext } from 'react';

import { isGoGameEnv } from 'App/constants';

import {
  Grid,
  Stack,
  ToggleButton,
  Typography,
  Section,
  HelpOutlineIcon,
} from '@gogame/components';
import {
  withForm,
  FormField,
  getIn,
  FormContext,
  SidePageV2,
} from '@gogame/form';

import {
  MY_CUSTOM_METRIC,
  isSystemMetric,
} from 'scenes/Trackers/Tracker/constants';
import {
  loadCalculatorMetrics,
  loadMetricsChartGroups,
  loadMetricsPartnersAvailability,
} from 'scenes/Trackers/Tracker/sources/metrics';
import SmartTooltip from 'scenes/Trackers/components/SmartTooltip';

import {
  DISPLAY_NAME,
  CUSTOM_METRIC,
  METRIC_TYPE,
  PRECISION,
  IS_HIGHLIGHT_LOWER,
  IS_ADVANCED_AVAILABLE,
  IS_AVAILABLE_FOR_CHART,
  IS_AVAILABLE_FOR_PARTNERS,
  PARTNERS_AVAILABILITY,
  CHART_AXIS_GROUP_ID,
  IS_PUBLIC,
  DESCRIPTION,
  GROUP_ID,
} from './constants';
import { customMetricValidationSchema } from './schema';

const getNameKey = (name = '') => name.replace(/\s+/, '').toLowerCase();

export const MetricForm = withForm({
  validationSchema: customMetricValidationSchema,
})(({
  dirty,
  onClose,
  title,
  subTitle,
  entityId,
  loading,
  values,
  validateField,
  initialValues,
  setFieldValue,
  onExited,
  open,
}) => {
  const isSystem = useMemo(
    () => isSystemMetric(values[GROUP_ID]),
    [values[GROUP_ID]]
  );

  const { hierarchyState } = useContext(FormContext);

  const validateUniqueName = useMemo(() => {
    const initialDisplayName = getIn(initialValues, DISPLAY_NAME);
    const metrics = getIn(hierarchyState, 'customMetric.data.metrics') || [];
    const metricNameKeys = metrics
      .filter(({ name }) => name !== initialDisplayName)
      .reduce(
        (keys, { name }) => ({
          ...keys,
          [getNameKey(name)]: true,
        }),
        {}
      );

    return (value = '') => {
      if (metricNameKeys.hasOwnProperty(getNameKey(value))) {
        return 'Name must be unique';
      }
    };
  }, [hierarchyState.customMetric]);

  const handleIsAvailableForChart = useCallback(
    (_, value) => {
      if (!value) {
        setFieldValue(CHART_AXIS_GROUP_ID, undefined);
      }
    },
    [setFieldValue]
  );

  const handleIsAvailableForPartners = useCallback(
    (_, value) => {
      if (!value) {
        setFieldValue(PARTNERS_AVAILABILITY, undefined);
      }
    },
    [setFieldValue]
  );

  useEffect(() => {
    if (dirty) {
      validateField(DISPLAY_NAME);
    }
  }, [dirty]);

  const isAllowPublic = isGoGameEnv && values?.[GROUP_ID] === MY_CUSTOM_METRIC;

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
    >
      <SidePageV2.Title title={title} subTitle={subTitle} entityId={entityId} />
      <SidePageV2.NewBody>
        <Section>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormField.TextField
                name={DISPLAY_NAME}
                label={'Name'}
                validate={validateUniqueName}
                helper={'60 characters max'}
                padding={0}
              />
            </Grid>
            <Grid item xs={6}>
              <Stack direction={'row'} spacing={3}>
                <FormField.TextField
                  name={PRECISION}
                  type={'number'}
                  placeholder={'0'}
                  label={'Decimals'}
                  padding={0}
                />
                <FormField.ToggleButtonGroup name={METRIC_TYPE} padding={'0'}>
                  <ToggleButton value={'number'} style={{ width: 50 }}>
                    123
                  </ToggleButton>
                  <ToggleButton value={'percent'} style={{ width: 50 }}>
                    %
                  </ToggleButton>
                  <ToggleButton value={'currency'} style={{ width: 50 }}>
                    $
                  </ToggleButton>
                </FormField.ToggleButtonGroup>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <FormField.TextField
                multiline={true}
                minRows={3}
                name={DESCRIPTION}
                padding={0}
                label={'Description'}
                helper={'Hint text. If empty, the formula text will be shown.'}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField.CustomMetricCalculator
                name={CUSTOM_METRIC}
                source={loadCalculatorMetrics}
                onlyPublicValid={values[IS_PUBLIC]}
                renderOptionMetric={(option) => (
                  <Stack
                    data-cy={`picker-option-${option.id}`}
                    spacing={1}
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    flex={'1 1 auto'}
                    zeroMinWidth={true}
                  >
                    <Typography noWrap={true} disableTooltip={true}>
                      {option.name}
                    </Typography>
                    <SmartTooltip
                      anchor={
                        <HelpOutlineIcon fontSize={'small'} color={'action'} />
                      }
                      name={option.name}
                      formula={option.displayFormula}
                      hint={option.tooltipHint}
                      showGlossary={option.showGlossary}
                      glossaryItems={option.glossaryItems}
                    />
                  </Stack>
                )}
                padding={0}
              />
            </Grid>
            <Grid item container xs={12}>
              <Grid item xs={6}>
                <Stack>
                  <Typography color={'textSecondary'} variant={'caption'}>
                    Options
                  </Typography>
                  <FormField.Checkbox
                    name={IS_HIGHLIGHT_LOWER}
                    label={'Lower value is a positive trend'}
                    padding={0}
                  />
                  <FormField.Checkbox
                    name={IS_ADVANCED_AVAILABLE}
                    label={'Available for advanced filter'}
                    padding={0}
                  />
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack>
                  <Typography color={'textSecondary'} variant={'caption'}>
                    Charts
                  </Typography>
                  <FormField.Switch
                    name={IS_AVAILABLE_FOR_CHART}
                    label={'Allowed for charts'}
                    padding={0}
                    onChange={handleIsAvailableForChart}
                  />
                  {values[IS_AVAILABLE_FOR_CHART] ? (
                    <FormField.Select
                      source={loadMetricsChartGroups}
                      basis={'50%'}
                      placeholder={'Chart group'}
                      padding={0}
                      name={CHART_AXIS_GROUP_ID}
                    />
                  ) : null}
                </Stack>
              </Grid>
            </Grid>
            <Grid item container xs={12}>
              {isSystem ? (
                <Grid item xs={6}>
                  <Stack>
                    <Typography color={'textSecondary'} variant={'caption'}>
                      Partners
                    </Typography>
                    <FormField.Checkbox
                      name={IS_AVAILABLE_FOR_PARTNERS}
                      label={'Available on PGG'}
                      padding={0}
                      onChange={handleIsAvailableForPartners}
                    />
                    {values[IS_AVAILABLE_FOR_PARTNERS] ? (
                      <FormField.CheckboxGroup
                        name={PARTNERS_AVAILABILITY}
                        padding={'0 0 0 30px'}
                        source={loadMetricsPartnersAvailability}
                        checkboxVertical={true}
                      />
                    ) : null}
                  </Stack>
                </Grid>
              ) : null}
              {isAllowPublic ? (
                <Grid item xs={6}>
                  <Stack>
                    <Typography color={'textSecondary'} variant={'caption'}>
                      Sharing
                    </Typography>
                    <FormField.Checkbox
                      name={IS_PUBLIC}
                      label={'Public metric'}
                      padding={0}
                    />
                  </Stack>
                </Grid>
              ) : null}
            </Grid>
          </Grid>
        </Section>
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
    </SidePageV2>
  );
});
